import { AiEngineList, MenuList } from "../../types/junoTypes";
import { TFunction } from "i18next";

// プロンプトの必須状態を表す型
export type PromptRequirement = "REQUIRED" | "OPTIONAL" | "NOT_NEEDED";

// エンジンとメニューの組み合わせに基づくプロンプト要件の定義
interface ModelRequirements {
  prompt: PromptRequirement;
  image: boolean;
  image2?: boolean;
}

// モデルごとのプロンプト要件を定義
const textToImageRequirements: Record<string, ModelRequirements> = {
  // デフォルト設定（明示的に指定されていないモデルはこれを使用）
  DEFAULT: {
    prompt: "REQUIRED",
    image: false,
  },
};

const imageToImageRequirements: Record<string, ModelRequirements> = {
  // デフォルト設定
  DEFAULT: {
    prompt: "REQUIRED",
    image: true,
  },
  // プロンプトが任意のモデル
  Flux11ProUltraRedux: {
    prompt: "OPTIONAL",
    image: true,
  },
  Flux11ProRedux: {
    prompt: "OPTIONAL",
    image: true,
  },
  // プロンプトが不要なモデル
  Flux1DevRedux: {
    prompt: "NOT_NEEDED",
    image: true,
  },
  Flux1SchnellRedux: {
    prompt: "NOT_NEEDED",
    image: true,
  },
  KolorsVTO: {
    prompt: "NOT_NEEDED",
    image: true,
    image2: true, // 2つめの画像も必要
  },
  Ghiblify: {
    prompt: "NOT_NEEDED",
    image: true,
  },
};

const upscaleRequirements: Record<string, ModelRequirements> = {
  // アップスケールではプロンプトは任意
  DEFAULT: {
    prompt: "OPTIONAL",
    image: true,
  },
};

const editorRequirements: Record<string, ModelRequirements> = {
  DEFAULT: {
    prompt: "REQUIRED",
    image: false,
  },
  GeminiFlashEdit: {
    prompt: "REQUIRED",
    image: true,
  },
};

// メニューに基づいて適切な要件マップを取得
const getRequirementsMap = (menu: MenuList): Record<string, ModelRequirements> => {
  switch (menu) {
    case "txt2img":
      return textToImageRequirements;
    case "img2img":
      return imageToImageRequirements;
    case "upscale":
      return upscaleRequirements;
    case "editor":
      return editorRequirements;
    default:
      return textToImageRequirements;
  }
};

// モデルの要件を取得
export const getModelRequirements = (aiEngine: AiEngineList, menu: MenuList): ModelRequirements => {
  const requirementsMap = getRequirementsMap(menu);
  return requirementsMap[aiEngine] || requirementsMap.DEFAULT;
};

// プロンプトの必須状態を取得
export const getPromptRequirement = (aiEngine: AiEngineList, menu: MenuList): PromptRequirement => {
  const requirements = getModelRequirements(aiEngine, menu);
  return requirements.prompt;
};

// プレースホルダーテキストを取得
export const getPlaceholderText = (t: TFunction, aiEngine: AiEngineList, menu: MenuList): string => {
  const promptRequirement = getPromptRequirement(aiEngine, menu);

  switch (promptRequirement) {
    case "REQUIRED":
      return t("juno.history.promptPlaceholder");
    case "OPTIONAL":
      return t("juno.history.optionalPromptPlaceholder");
    case "NOT_NEEDED":
      return t("juno.history.notNeededPlaceholder");
    default:
      return t("juno.history.promptPlaceholder");
  }
};

// 入力値バリデーション
export const validateInputs = (
  params: {
    aiEngine: AiEngineList;
    menu: MenuList;
    prompt: string;
    image: string | null;
    image2: string | null;
    image3?: string | null;
    image4?: string | null;
    editorInputImage: string | null;
    editorInputMask: string | null;
  },
  t: TFunction
): { isValid: boolean; errorMessages: string[] } => {
  const { aiEngine, menu, prompt, image, image2, editorInputImage, editorInputMask } = params;
  const requirements = getModelRequirements(aiEngine, menu);
  const errorMessages: string[] = [];

  // プロンプトのバリデーション
  if (requirements.prompt === "REQUIRED" && !prompt.trim()) {
    errorMessages.push("・" + t("juno.validation.required.prompt"));
  }

  if (prompt.trim().length > 1000) {
    errorMessages.push("・" + t("juno.validation.prompt"));
  }

  // 画像のバリデーション
  if (requirements.image && !image) {
    errorMessages.push("・" + t("juno.validation.image"));
  }

  // 2つ目の画像のバリデーション
  if (requirements.image2 && !image2) {
    errorMessages.push("・" + t("juno.validation.image"));
  }

  // エディターモードの場合の追加バリデーション
  if (menu === "editor") {
    if (aiEngine === "GeminiFlashEdit") {
      if (!image) {
        errorMessages.push("・" + t("juno.validation.image"));
      }
    } else {
      if (!editorInputImage || !editorInputMask) {
        errorMessages.push("・" + t("juno.validation.editorImage"));
      }
    }
  }

  return {
    isValid: errorMessages.length === 0,
    errorMessages,
  };
};

// 送信ボタンの無効状態をチェック
export const isSendButtonDisabled = (
  waitingGenerate: boolean,
  generatingPrompt: boolean,
  aiEngine: AiEngineList,
  menu: MenuList,
  localPrompt: string
): boolean => {
  if (waitingGenerate || generatingPrompt) {
    return true;
  }

  const promptRequirement = getPromptRequirement(aiEngine, menu);
  return promptRequirement === "REQUIRED" && localPrompt.trim().length === 0;
};

// 入力フィールドが無効かどうかをチェック
export const isInputFieldDisabled = (aiEngine: AiEngineList, menu: MenuList): boolean => {
  const promptRequirement = getPromptRequirement(aiEngine, menu);
  return promptRequirement === "NOT_NEEDED";
};

// フォームのプロパティバリデーション
export const validateFormInputs = (
  aiEngine: AiEngineList,
  menu: MenuList,
  t: TFunction
): ((value: string) => { isValid: boolean; message: string | null }) => {
  const promptRequirement = getPromptRequirement(aiEngine, menu);

  return (value: string) => {
    if (promptRequirement === "REQUIRED" && !value.trim()) {
      return { isValid: false, message: t("juno.validation.required.prompt") };
    }

    if (value.trim().length > 1000) {
      return { isValid: false, message: t("juno.validation.prompt") };
    }

    return { isValid: true, message: null };
  };
};
